import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { BsEyeFill, BsPencilFill, BsTrashFill } from "react-icons/bs";
import Swal from "sweetalert2";

const actionButtonStyles = (type) => {
  const colors = {
    view: { bg: '#eff6ff', color: '#2563eb', hoverBg: '#2563eb', hoverColor: '#ffffff' },
    edit: { bg: '#f0fdf4', color: '#16a34a', hoverBg: '#16a34a', hoverColor: '#ffffff' },
    delete: { bg: '#fef2f2', color: '#dc2626', hoverBg: '#dc2626', hoverColor: '#ffffff' }
  };
  const activeColor = colors[type];
  return {
    width: 32,
    height: 32,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: activeColor.bg,
    color: activeColor.color,
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid transparent',
    '&:hover': {
      background: activeColor.hoverBg,
      color: activeColor.hoverColor,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    }
  };
};

const Card = ({ head, title, subtitle, onAction, children, hideButtons = [1, 1, 1] }) => {
  return (
    <Box
      className="move-top"
      sx={{
        p: 2.5,
        width: '100%',
        height: '100%',
        minWidth: 240,
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '18px',
        boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.05), 0 2px 6px -1px rgba(15, 23, 42, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 16px 36px -4px rgba(15, 23, 42, 0.09), 0 6px 12px -2px rgba(15, 23, 42, 0.04)',
          borderColor: 'rgba(99, 102, 241, 0.3)'
        }
      }}
    >
      {/* SaaS Card Top Bar: Status (head) & Action Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.5, minHeight: 32 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, flexGrow: 1 }}>
          {head || null}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', flexShrink: 0 }}>
          {hideButtons[0] === 1 && (
            <Tooltip title="View Details">
              <Box sx={actionButtonStyles('view')} onClick={() => onAction('more-info')}>
                <BsEyeFill size={14} />
              </Box>
            </Tooltip>
          )}
          {hideButtons[1] === 1 && (
            <Tooltip title="Edit">
              <Box sx={actionButtonStyles('edit')} onClick={() => onAction('edit')}>
                <BsPencilFill size={12} />
              </Box>
            </Tooltip>
          )}
          {hideButtons[2] === 1 && (
            <Tooltip title="Delete">
              <Box
                sx={actionButtonStyles('delete')}
                onClick={(e) => {
                  e.stopPropagation();
                  Swal.fire({
                    title: "Are you sure?",
                    text: "You won't be able to revert this!",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3b82f6",
                    cancelButtonColor: "#ef4444",
                    confirmButtonText: "Yes, delete it!"
                  }).then((result) => {
                    if (result.isConfirmed) {
                      onAction("delete");
                    }
                  });
                }}
              >
                <BsTrashFill size={12} />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* SaaS Card Title Bar: Avatar & Heading */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            color: '#2563eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '18px',
            flexShrink: 0,
            border: '1px solid #bfdbfe',
            boxShadow: '0 2px 4px rgba(37, 99, 235, 0.08)'
          }}
        >
          {title ? title.charAt(0).toUpperCase() : '•'}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 750,
              fontSize: '16px',
              color: '#0f172a',
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word'
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            typeof subtitle === 'string' || typeof subtitle === 'number' ? (
              <Typography
                component="div"
                sx={{
                  color: '#64748b',
                  fontSize: '13px',
                  mt: 0.4,
                  fontWeight: 500,
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {subtitle}
              </Typography>
            ) : (
              <Box sx={{ mt: 0.6 }}>
                {subtitle}
              </Box>
            )
          )}
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ borderTop: "1px solid #f1f5f9", mb: 2 }} />

      {/* Details / Children */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
        {children}
      </Box>
    </Box>
  );
};

export default Card;